import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { WeeklyTimesheet, WeeklyTimesheetEntry } from '../types/timesheet.ts';
import { Employee } from '../types/employee.ts';
import { Calendar, Clock, Users, AlertTriangle, CheckCircle, Eye, Phone, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';

interface EmployeeWeekSummary {
  employeeId: string;
  employeeName: string;
  jobs: {
    jobName: string;
    jobNumber?: string;
    timesheetId: string;
    hours: {
      monday: { regular: number; overtime: number; total: number };
      tuesday: { regular: number; overtime: number; total: number };
      wednesday: { regular: number; overtime: number; total: number };
      thursday: { regular: number; overtime: number; total: number };
      friday: { regular: number; overtime: number; total: number };
      saturday: { regular: number; overtime: number; total: number };
      sunday: { regular: number; overtime: number; total: number };
    };
    activities: {
      monday: string[];
      tuesday: string[];
      wednesday: string[];
      thursday: string[];
      friday: string[];
      saturday: string[];
      sunday: string[];
    };
  }[];
  totalHours: number;
  totalRegularHours: number;
  totalOvertimeHours: number;
  daysWorked: number;
  conflicts: string[];
  missingDays: string[];
  employeeInfo?: Employee;
}

const EmployeeTimeReview: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [weekEndingDate, setWeekEndingDate] = useState(() => {
    // Default to next Sunday
    const today = new Date();
    const daysUntilSunday = (7 - today.getDay()) % 7;
    const nextSunday = new Date(today);
    nextSunday.setDate(today.getDate() + daysUntilSunday);
    return nextSunday.toISOString().split('T')[0];
  });
  
  const [timesheets, setTimesheets] = useState<WeeklyTimesheet[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeeSummaries, setEmployeeSummaries] = useState<EmployeeWeekSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    if (weekEndingDate) {
      loadTimesheetsForWeek();
    }
  }, [weekEndingDate]);

  useEffect(() => {
    if (timesheets.length > 0 && employees.length > 0) {
      generateEmployeeSummaries();
    }
  }, [timesheets, employees]);

  const loadEmployees = async () => {
    try {
      const employeesRef = collection(db, 'employees');
      const snapshot = await getDocs(employeesRef);
      
      const employeeData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        hireDate: doc.data().hireDate?.toDate(),
        terminationDate: doc.data().terminationDate?.toDate(),
        licenseExpiration: doc.data().licenseExpiration?.toDate(),
      })) as Employee[];

      setEmployees(employeeData);
    } catch (err) {
      console.error('Error loading employees:', err);
      setError('Failed to load employees');
    }
  };

  const loadTimesheetsForWeek = async () => {
    try {
      setLoading(true);
      setError(null);

      const targetDate = new Date(weekEndingDate);
      const timesheetsRef = collection(db, 'weeklyTimesheets');
      const q = query(
        timesheetsRef,
        where('weekEnding', '>=', targetDate),
        where('weekEnding', '<', new Date(targetDate.getTime() + 24 * 60 * 60 * 1000))
      );
      
      const snapshot = await getDocs(q);
      
      const timesheetData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        weekEnding: doc.data().weekEnding?.toDate() || new Date(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as WeeklyTimesheet[];

      setTimesheets(timesheetData);
    } catch (err) {
      console.error('Error loading timesheets:', err);
      setError('Failed to load timesheets for selected week');
    } finally {
      setLoading(false);
    }
  };

  const generateEmployeeSummaries = () => {
    const summaryMap = new Map<string, EmployeeWeekSummary>();

    timesheets.forEach(timesheet => {
      timesheet.entries.forEach(entry => {
        const key = `${entry.employeeId || entry.employeeName}`;
        
        if (!summaryMap.has(key)) {
          summaryMap.set(key, {
            employeeId: entry.employeeId || '',
            employeeName: entry.employeeName,
            jobs: [],
            totalHours: 0,
            totalRegularHours: 0,
            totalOvertimeHours: 0,
            daysWorked: 0,
            conflicts: [],
            missingDays: [],
            employeeInfo: employees.find(emp => emp.id === entry.employeeId)
          });
        }

        const summary = summaryMap.get(key)!;
        
        // Create job entry
        const jobEntry = {
          jobName: timesheet.jobName,
          jobNumber: timesheet.jobNumber,
          timesheetId: timesheet.id,
          hours: {
            monday: getHoursBreakdown(entry, 'monday'),
            tuesday: getHoursBreakdown(entry, 'tuesday'),
            wednesday: getHoursBreakdown(entry, 'wednesday'),
            thursday: getHoursBreakdown(entry, 'thursday'),
            friday: getHoursBreakdown(entry, 'friday'),
            saturday: getHoursBreakdown(entry, 'saturday'),
            sunday: getHoursBreakdown(entry, 'sunday')
          },
          activities: {
            monday: getActivitiesForDay(entry, 'monday'),
            tuesday: getActivitiesForDay(entry, 'tuesday'),
            wednesday: getActivitiesForDay(entry, 'wednesday'),
            thursday: getActivitiesForDay(entry, 'thursday'),
            friday: getActivitiesForDay(entry, 'friday'),
            saturday: getActivitiesForDay(entry, 'saturday'),
            sunday: getActivitiesForDay(entry, 'sunday')
          }
        };

        summary.jobs.push(jobEntry);

        // Update totals
        dayKeys.forEach(day => {
          const dayHours = jobEntry.hours[day];
          summary.totalHours += dayHours.total;
          summary.totalRegularHours += dayHours.regular;
          summary.totalOvertimeHours += dayHours.overtime;
        });
      });
    });

    // Post-process to detect conflicts and missing days
    summaryMap.forEach(summary => {
      detectConflicts(summary);
      detectMissingDays(summary);
      summary.daysWorked = dayKeys.filter(day => 
        summary.jobs.some(job => job.hours[day].total > 0)
      ).length;
    });

    const sortedSummaries = Array.from(summaryMap.values()).sort((a, b) => 
      a.employeeName.localeCompare(b.employeeName)
    );

    setEmployeeSummaries(sortedSummaries);
  };

  const getHoursBreakdown = (entry: WeeklyTimesheetEntry, day: keyof WeeklyTimesheetEntry['hours']) => {
    const total = entry.hours[day] || 0;
    const regular = entry.regularHours?.[day] || 0;
    const overtime = entry.overtimeHours?.[day] || 0;
    
    // If we have breakdown data, use it; otherwise assume all regular
    if (entry.regularHours && entry.overtimeHours) {
      return { regular, overtime, total: regular + overtime };
    } else {
      return { regular: total, overtime: 0, total };
    }
  };

  const getActivitiesForDay = (entry: WeeklyTimesheetEntry, day: keyof WeeklyTimesheetEntry['activities']) => {
    const activities = entry.activities[day] || [];
    return activities.map(activity => 
      `${activity.activityCode}: ${activity.activityDescription}`.trim()
    ).filter(desc => desc !== ':');
  };

  const detectConflicts = (summary: EmployeeWeekSummary) => {
    const conflicts: string[] = [];

    dayKeys.forEach(day => {
      const jobsWorkedToday = summary.jobs.filter(job => job.hours[day].total > 0);
      if (jobsWorkedToday.length > 1) {
        const jobNames = jobsWorkedToday.map(job => job.jobName).join(', ');
        conflicts.push(`${dayNames[dayKeys.indexOf(day)]}: Multiple jobs (${jobNames})`);
      }
    });

    summary.conflicts = conflicts;
  };

  const detectMissingDays = (summary: EmployeeWeekSummary) => {
    const workDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    const missingDays: string[] = [];

    workDays.forEach((dayName, index) => {
      const dayKey = dayKeys[index];
      const totalHours = summary.jobs.reduce((sum, job) => sum + job.hours[dayKey].total, 0);
      if (totalHours === 0) {
        missingDays.push(dayName);
      }
    });

    summary.missingDays = missingDays;
  };

  const formatHours = (regular: number, overtime: number) => {
    if (regular === 0 && overtime === 0) return '-';
    
    return (
      <div className="text-center">
        <div className="text-sm font-medium text-gray-900">{regular || 0}</div>
        {overtime > 0 && (
          <div className="text-xs text-orange-600 font-semibold">+{overtime} OT</div>
        )}
      </div>
    );
  };

  const getWeekDateRange = () => {
    const endDate = new Date(weekEndingDate);
    const startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - 6);
    return {
      start: startDate,
      end: endDate,
      formatted: `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
    };
  };

  const weekRange = getWeekDateRange();

  const handlePrintToPDF = async () => {
    const element = document.getElementById('time-review-content');
    
    if (!element) {
      alert('Unable to find content to print');
      return;
    }

    // Hide non-printable elements
    const nonPrintables = document.querySelectorAll('.no-print');
    nonPrintables.forEach(el => {
      (el as HTMLElement).style.display = 'none';
    });

    const opt = {
      margin: [10, 10, 10, 10],
      filename: `Employee_Time_Review_${weekEndingDate}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        letterRendering: true,
        width: 1200,
        height: element.scrollHeight
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'landscape' 
      }
    };

    try {
      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      // Show non-printable elements again
      nonPrintables.forEach(el => {
        (el as HTMLElement).style.display = '';
      });
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6" id="time-review-content">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Employee Time Review</h1>
            <p className="text-gray-600 mt-1">Review employee hours and detect scheduling conflicts</p>
          </div>
          <div className="flex space-x-3 no-print">
            <button
              onClick={handlePrintToPDF}
              className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 border border-blue-600 rounded-md transition-colors flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>Download PDF</span>
            </button>
            <button
              onClick={() => navigate('/timesheets')}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Back to Timesheets
            </button>
          </div>
        </div>
      </div>

      {/* Week Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 no-print">
        <div className="flex items-center space-x-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Week Ending Date (Sunday)
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="date"
                value={weekEndingDate}
                onChange={(e) => setWeekEndingDate(e.target.value)}
                className="pl-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="pt-6">
            <div className="text-sm text-gray-600">
              <strong>Week Range:</strong> {weekRange.formatted}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      ) : employeeSummaries.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No timesheets found</h3>
          <p className="mt-1 text-sm text-gray-500">
            No employee time data found for the selected week ending {new Date(weekEndingDate).toLocaleDateString()}.
          </p>
        </div>
      ) : (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <div className="text-2xl font-bold text-gray-900">{employeeSummaries.length}</div>
                  <div className="text-sm text-gray-600">Employees</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <AlertTriangle className="h-8 w-8 text-red-600" />
                <div className="ml-4">
                  <div className="text-2xl font-bold text-gray-900">
                    {employeeSummaries.filter(emp => emp.conflicts.length > 0).length}
                  </div>
                  <div className="text-sm text-gray-600">Conflicts</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <div className="text-2xl font-bold text-gray-900">
                    {employeeSummaries.reduce((sum, emp) => sum + emp.totalHours, 0).toFixed(1)}
                  </div>
                  <div className="text-sm text-gray-600">Total Hours</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <Phone className="h-8 w-8 text-orange-600" />
                <div className="ml-4">
                  <div className="text-2xl font-bold text-gray-900">
                    {employeeSummaries.filter(emp => emp.missingDays.length > 0).length}
                  </div>
                  <div className="text-sm text-gray-600">Need Follow-up</div>
                </div>
              </div>
            </div>
          </div>

          {/* Employee Table */}
          <div className="bg-white shadow-xl rounded-xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 text-white">
              <h2 className="text-2xl font-bold text-center mb-2">Employee Time Summary</h2>
              <p className="text-center text-blue-100">
                Week of {weekRange.formatted}
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">
                      Employee
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Mon<br/><small>R/OT</small>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Tue<br/><small>R/OT</small>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Wed<br/><small>R/OT</small>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Thu<br/><small>R/OT</small>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Fri<br/><small>R/OT</small>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Sat<br/><small>R/OT</small>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Sun<br/><small>R/OT</small>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Total<br/><small>R/OT</small>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {employeeSummaries.map((summary, index) => {
                    const hasConflicts = summary.conflicts.length > 0;
                    const hasMissingDays = summary.missingDays.length > 0;
                    const isExpanded = selectedEmployee === summary.employeeName;
                    
                    return (
                      <React.Fragment key={index}>
                        <tr className={`hover:bg-gray-50 ${hasConflicts ? 'bg-red-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {summary.employeeName}
                              </div>
                              <div className="text-xs text-gray-500">
                                {summary.jobs.length} job{summary.jobs.length !== 1 ? 's' : ''} • {summary.daysWorked} days
                              </div>
                              {summary.employeeInfo && (
                                <div className="text-xs text-gray-400">
                                  ID: {summary.employeeInfo.employeeNumber || 'N/A'}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        
                        {dayKeys.map(day => {
                          const dayTotalRegular = summary.jobs.reduce((sum, job) => sum + job.hours[day].regular, 0);
                          const dayTotalOvertime = summary.jobs.reduce((sum, job) => sum + job.hours[day].overtime, 0);
                          const jobsForDay = summary.jobs.filter(job => job.hours[day].total > 0);
                          const hasConflictToday = jobsForDay.length > 1;
                          
                          return (
                            <td key={day} className={`px-4 py-4 whitespace-nowrap ${hasConflictToday ? 'bg-red-100' : ''}`}>
                              {formatHours(dayTotalRegular, dayTotalOvertime)}
                              {hasConflictToday && (
                                <div className="text-xs text-red-600 font-semibold">CONFLICT</div>
                              )}
                            </td>
                          );
                        })}
                        
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="text-sm font-bold text-gray-900">{summary.totalRegularHours.toFixed(1)}</div>
                          {summary.totalOvertimeHours > 0 && (
                            <div className="text-xs text-orange-600 font-semibold">+{summary.totalOvertimeHours.toFixed(1)} OT</div>
                          )}
                          <div className="text-xs text-gray-500 border-t pt-1 mt-1">
                            Total: {summary.totalHours.toFixed(1)}
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex flex-col items-center space-y-1">
                            {hasConflicts && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Conflicts
                              </span>
                            )}
                            {hasMissingDays && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <Phone className="h-3 w-3 mr-1" />
                                Missing Days
                              </span>
                            )}
                            {!hasConflicts && !hasMissingDays && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Good
                              </span>
                            )}
                            <button
                              onClick={() => setSelectedEmployee(selectedEmployee === summary.employeeName ? null : summary.employeeName)}
                              className="text-xs text-blue-600 hover:text-blue-800 flex items-center no-print"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Details
                            </button>
                          </div>
                        </td>
                        </tr>
                        
                        {/* Expandable Details Row */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={10} className="px-0 py-0">
                              <div className="bg-gray-50 border-l-4 border-blue-500">
                                <div className="p-6">
                                  {/* Conflicts and Missing Days */}
                                  {(summary.conflicts.length > 0 || summary.missingDays.length > 0) && (
                                    <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {summary.conflicts.length > 0 && (
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                          <h4 className="font-semibold text-red-800 flex items-center">
                                            <AlertTriangle className="h-4 w-4 mr-2" />
                                            Scheduling Conflicts
                                          </h4>
                                          <ul className="mt-2 text-sm text-red-700 space-y-1">
                                            {summary.conflicts.map((conflict, conflictIndex) => (
                                              <li key={conflictIndex}>• {conflict}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                      
                                      {summary.missingDays.length > 0 && (
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                          <h4 className="font-semibold text-yellow-800 flex items-center">
                                            <Phone className="h-4 w-4 mr-2" />
                                            Follow-up Needed
                                          </h4>
                                          <p className="mt-2 text-sm text-yellow-700">
                                            Missing hours on: <strong>{summary.missingDays.join(', ')}</strong>
                                          </p>
                                          <p className="mt-1 text-xs text-yellow-600">
                                            Call to confirm: "So you didn't work {summary.missingDays.join(' and ')}?"
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Job Breakdown */}
                                  <div className="space-y-4">
                                    <h4 className="font-semibold text-gray-900">Jobs Worked This Week</h4>
                                    {summary.jobs.map((job, jobIndex) => (
                                      <div key={jobIndex} className="border border-gray-200 rounded-lg p-4 bg-white">
                                        <div className="flex justify-between items-center mb-3">
                                          <div>
                                            <h5 className="font-medium text-gray-900">{job.jobName}</h5>
                                            {job.jobNumber && (
                                              <p className="text-sm text-gray-600">Job #{job.jobNumber}</p>
                                            )}
                                          </div>
                                          <button
                                            onClick={() => navigate(`/timesheets/entry/${job.timesheetId}`)}
                                            className="text-sm text-blue-600 hover:text-blue-800 no-print"
                                          >
                                            Edit Timesheet
                                          </button>
                                        </div>
                                        
                                        <div className="grid grid-cols-7 gap-2 text-sm">
                                          {dayKeys.map((day, dayIndex) => (
                                            <div key={day} className="text-center">
                                              <div className="font-medium text-gray-700">{dayNames[dayIndex]}</div>
                                              <div className="mt-1">
                                                {formatHours(job.hours[day].regular, job.hours[day].overtime)}
                                              </div>
                                              {job.activities[day].length > 0 && (
                                                <div className="mt-1 text-xs text-gray-500">
                                                  {job.activities[day].map((activity, actIndex) => (
                                                    <div key={actIndex}>{activity}</div>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

        </>
      )}
    </div>
  );
};

export default EmployeeTimeReview;